/** Shared so `beforeSend` can recognize the events `capture404NotFound` produces */
export const NOT_FOUND_FINGERPRINT = '404-not-found';

// `bot\b`, not `bot`, so that device names like "Cubot_X19" don't match.
// `read-aloud` is Google's text-to-speech fetcher. It renders the page a user has open without their session,
// so the 404s and failed images it reports are duplicates of that user's own reports, or images it never loads.
const AUTOMATED_USER_AGENT =
  /bot\b|crawler|crawling|spider|slurp|headless|puppeteer|playwright|phantomjs|scrapy|curl\/|wget|python-requests|go-http-client|lighthouse|read-aloud/i;

type AutomationSignals = { userAgent?: string; webdriver?: boolean };

/**
 * Best-effort only - every signal is trivially spoofable, so never use this for access control.
 * Scrapers sending a stock user agent are indistinguishable from real users here.
 */
export const isAutomatedClient = (nav = globalThis.navigator as AutomationSignals | undefined): boolean => {
  if (!nav) {
    return false;
  }
  return nav.webdriver === true || AUTOMATED_USER_AGENT.test(nav.userAgent ?? '');
};

/**
 * Drops 404 events from bots enumerating asset ids, which have flooded Sentry with tens of
 * thousands of events in a day. Every other kind of event from the same client still goes
 * through. Returns `null` for events to discard, per Sentry's `beforeSend` contract.
 */
export const dropNoiseFromAutomatedClients = <T extends { fingerprint?: string[] }>(
  event: T,
  nav?: AutomationSignals
): T | null => (event.fingerprint?.includes(NOT_FOUND_FINGERPRINT) && isAutomatedClient(nav) ? null : event);

/** Internal-only test player, see `PlayerTestPage`. Used by the route def so the two can't drift apart. */
export const DEV_PLAYER_PATH = '/dev/player';

//Drops everything reported while on the dev player

export const dropEventsFromDevPlayer = <T>(event: T, pathname = globalThis.location?.pathname ?? ''): T | null =>
  pathname.replace(/\/+$/, '') === DEV_PLAYER_PATH ? null : event;

export const isError = (value: any): boolean => {
  // Yanked from https://git.io/vS8DV re-used under CC0
  // with the same modifications as in raven.
  switch ({}.toString.call(value)) {
    case '[object Error]':
      return true;
    case '[object Exception]':
      return true;
    case '[object DOMException]':
      return true;
    default:
      return value instanceof Error;
  }
};
